import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Autocomplete, Box, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAgencies } from "../../../pages/Profile/v2/ProfessionalInfo/useAgencies";
import { INK } from "../../../theme/tokens";
import type { AgencyWage, AgencyWageBulkUpsertRequest, AgencyWageBulkUpsertResponse } from "../../../utils/models";
import type { MultiSortOption, SortingState } from "../../../utils/multiColumnSorting";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../UIToolkit/Tables/SystemDataGrid";
import { type AgencyRow, agencyCellText, agencyDisplayColumns, agencyRowsOf } from "./agencyWageColumns";

/**
 * Orders two agencies by one role's rate.
 *
 * A blank rate is "not configured", not zero (see `AgencyRow.roleWages`), so it sorts as absent
 * rather than as the cheapest agency. Nulls sink in ascending order; a descending sort surfaces
 * them first, which is the honest inverse.
 */
function compareRoleWage(a: AgencyRow, b: AgencyRow, roleKey: string): 1 | 0 | -1 {
	const left = a.roleWages?.[roleKey]?.wage ?? null;
	const right = b.roleWages?.[roleKey]?.wage ?? null;
	if (left === right) return 0;
	if (left === null) return 1;
	if (right === null) return -1;
	return left > right ? 1 : -1;
}

type AgencyWagesTableProps = {
	departmentId: string;
	agencyWages: AgencyWage[];
	isSaving: boolean;
	onSave: (request: AgencyWageBulkUpsertRequest) => Promise<AgencyWageBulkUpsertResponse>;
	onDelete: (agencyWageId: string) => Promise<void>;
};

export const AgencyWagesTable: FC<AgencyWagesTableProps> = ({ departmentId, agencyWages, isSaving, onSave, onDelete }) => {
	const { t } = useTranslation();
	const { departmentNurseTypes } = useHospitalData();
	const { agenciesData, createAgency, allAgencies } = useAgencies();
	const { showSnackbar } = useSnackbar();

	const [editingCell, setEditingCell] = useState<{ agencyName: string; field: string } | null>(null);
	const [editValues, setEditValues] = useState<Record<string, number>>({});
	const [editingRowId, setEditingRowId] = useState<string | null>(null);
	const [editedRow, setEditedRow] = useState<AgencyRow | null>(null);
	const [newAgency, setNewAgency] = useState<{ agencyName: string; roleWages: Record<string, { wage: number }> } | null>(null);
	const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; row: AgencyRow | null }>({ open: false, row: null });

	const displayColumns = useMemo(() => agencyDisplayColumns(departmentNurseTypes), [departmentNurseTypes]);

	const agencyRows = useMemo<AgencyRow[]>(() => agencyRowsOf(agencyWages), [agencyWages]);

	const availableAgencies = useMemo(
		() => allAgencies.map((agency) => agency.name).filter((name) => !agencyRows.find((row) => row.agencyName === name)),
		[allAgencies, agencyRows],
	);

	const handleCellDoubleClick = useCallback(
		(agencyName: string, backendRole: string, currentValue: number | null) => {
			if (editingRowId === agencyName) return;
			setEditingCell({ agencyName, field: backendRole });
			// Seed only a real value; a blank cell stays blank so blurring without typing saves nothing.
			setEditValues(currentValue === null ? {} : { [`${agencyName}-${backendRole}`]: currentValue });
		},
		[editingRowId],
	);

	const handleEditRow = useCallback((row: AgencyRow) => {
		setEditingRowId(row.agencyName);
		setEditedRow({ ...row, roleWages: { ...row.roleWages } });
		setEditingCell(null);
	}, []);

	const handleCancelRowEdit = useCallback(() => {
		setEditingRowId(null);
		setEditedRow(null);
	}, []);

	const handleSaveRowEdit = useCallback(async () => {
		if (!editedRow) return;
		// Every edited role goes in ONE request. Splitting a row save into a batch-update plus a create
		// per new role is what produced a success and a failure snackbar from a single click.
		const entries = displayColumns
			.filter((col) => editedRow.roleWages[col.backend] !== undefined)
			.map((col) => ({ role: col.backend, wage: editedRow.roleWages[col.backend].wage }));
		if (entries.length === 0) {
			setEditingRowId(null);
			setEditedRow(null);
			return;
		}
		await onSave({ departmentId, agencyName: editedRow.agencyName, entries });
		setEditingRowId(null);
		setEditedRow(null);
	}, [editedRow, displayColumns, onSave, departmentId]);

	const handleRowRoleWageChange = useCallback((backendRole: string, wage: number) => {
		setEditedRow((prev) => (prev ? { ...prev, roleWages: { ...prev.roleWages, [backendRole]: { ...prev.roleWages[backendRole], wage } } } : null));
	}, []);

	const handleCellBlur = useCallback(
		async (agencyName: string, backendRole: string) => {
			const key = `${agencyName}-${backendRole}`;
			const newValue = editValues[key];
			setEditingCell(null);
			if (newValue === undefined) return;
			// Same single-request path as a row save, just with one entry — the backend upserts, so it
			// no longer matters whether this cell already has a wage id.
			await onSave({ departmentId, agencyName, entries: [{ role: backendRole, wage: newValue }] });
		},
		[editValues, departmentId, onSave],
	);

	const handleDeleteClick = useCallback((row: AgencyRow) => setDeleteConfirmation({ open: true, row }), []);
	const handleDeleteCancel = useCallback(() => setDeleteConfirmation({ open: false, row: null }), []);

	const handleDeleteConfirm = useCallback(async () => {
		if (!deleteConfirmation.row) return;
		const deletePromises = Object.values(deleteConfirmation.row.roleWages)
			.filter((rw) => rw.wageId)
			.map((rw) => onDelete(rw.wageId as string));
		try {
			// Per-row telemetry and the failure snackbar live in the delete mutation itself, so this only
			// owns the dialog and the one summary message for the whole row.
			await Promise.all(deletePromises);
			setDeleteConfirmation({ open: false, row: null });
			showSnackbar(t("agency.wage.delete.success"), "success");
		} catch {
			// Leave the dialog open so the partially-deleted row stays visible and can be retried.
		}
	}, [deleteConfirmation.row, onDelete, showSnackbar, t]);

	const handleOpenAddDialog = useCallback(() => setNewAgency({ agencyName: "", roleWages: {} }), []);
	const handleCloseAddDialog = useCallback(() => setNewAgency(null), []);
	const handleNewAgencyInput = useCallback((agencyName: string) => setNewAgency((prev) => (prev ? { ...prev, agencyName } : null)), []);
	const handleNewRoleWageChange = useCallback((backendRole: string, wage: number) => {
		setNewAgency((prev) => (prev ? { ...prev, roleWages: { ...prev.roleWages, [backendRole]: { wage } } } : null));
	}, []);

	const handleSaveNew = useCallback(async () => {
		if (!newAgency?.agencyName) {
			showSnackbar(t("agency.wages.error.no.name"), "error");
			return;
		}
		const agencyName = newAgency.agencyName.trim();
		if (!agencyName) {
			showSnackbar(t("agency.wages.error.no.name"), "error");
			return;
		}
		if (agencyRows.find((r) => r.agencyName.toLowerCase() === agencyName.toLowerCase())) {
			showSnackbar(t("agency.wages.error.duplicate", { agencyName }), "error");
			return;
		}
		// Ensure the agency exists globally before creating wage entries for it. A duplicate-name
		// 409 from /agencies is recoverable (someone else may have created it); other errors abort
		// so we don't end up with wage rows pointing at a missing parent agency.
		const existingAgency = agenciesData.find((a) => a.name.toLowerCase() === agencyName.toLowerCase());
		if (!existingAgency) {
			try {
				await createAgency(agencyName);
			} catch (error) {
				if (error && typeof error === "object" && "response" in error) {
					const axiosError = error as { response?: { data?: { detail?: string } } };
					const errorMsg = axiosError.response?.data?.detail;
					if (errorMsg?.toLowerCase().includes("already exists")) {
						showSnackbar(t("agency.create.failed"), "warning");
					} else {
						showSnackbar(errorMsg || t("agency.create.failed"), "error");
						return;
					}
				}
			}
		}
		const entries = displayColumns
			.filter((col) => newAgency.roleWages?.[col.backend]?.wage !== undefined)
			.map((col) => ({ role: col.backend, wage: newAgency.roleWages?.[col.backend]?.wage }));
		if (entries.length === 0) {
			showSnackbar(t("agency.wages.error.no.wages"), "error");
			return;
		}
		await onSave({ departmentId, agencyName, entries });
		setNewAgency(null);
	}, [newAgency, agencyRows, agenciesData, createAgency, onSave, departmentId, displayColumns, showSnackbar, t]);

	const sortingOptions = useMemo<MultiSortOption<AgencyRow>[]>(
		() => [
			{ label: t("agency"), sortColumns: ["agencyName"] },
			...displayColumns.map((col) => ({
				label: col.display,
				// Identity only. The `role:` columns are synthetic — the rate lives in `roleWages`, not a
				// top-level row field — and a `customSortFunction` skips the per-column value walk (see the
				// SortingState contract), so `sortColumns` only has to match the column's `field` for the
				// header to find this option. Hence the cast: the string is a column key, not a row key.
				sortColumns: [`role:${col.backend}` as keyof AgencyRow],
				customSortFunction: (a: AgencyRow, b: AgencyRow) => compareRoleWage(a, b, col.backend),
			})),
		],
		[t, displayColumns],
	);
	const defaultSortState = useMemo<SortingState<AgencyRow>[]>(() => [{ sortColumns: ["agencyName"], sortOrder: "asc", label: t("agency") }], [t]);

	const columns = useMemo<GridColDef<AgencyRow>[]>(() => {
		const cols: GridColDef<AgencyRow>[] = [
			{
				field: "agencyName",
				headerName: t("agency"),
				flex: 18,
				minWidth: 200,
				renderCell: ({ row }) => <Typography sx={{ fontSize: "14px", fontWeight: 600, color: INK.SECONDARY }}>{row.agencyName}</Typography>,
			},
		];

		for (const col of displayColumns) {
			cols.push({
				// Prefix with `role:` so the column key can't collide with a literal agency-row property
				// (e.g. if a backend role were ever named "agencyName") and so MUI X doesn't try to read
				// `row[col.backend]` for a non-existent top-level field.
				field: `role:${col.backend}`,
				headerName: col.display,
				flex: 10,
				minWidth: 100,
				renderCell: ({ row }) => {
					const isEditingRow = editingRowId === row.agencyName;
					const isCellEditing = editingCell?.agencyName === row.agencyName && editingCell.field === col.backend;
					const sourceWages = isEditingRow ? editedRow?.roleWages : row.roleWages;
					const roleWage = sourceWages?.[col.backend] ?? { wage: null };
					// A blank rate shows as an empty input rather than a fabricated 0, so opening the editor
					// on an unset cell and saving doesn't silently write a real zero rate.
					const editableWage = roleWage.wage ?? "";

					if (isEditingRow) {
						return (
							<TextField
								type="number"
								value={editableWage}
								onChange={(e) => handleRowRoleWageChange(col.backend, Number.parseFloat(e.target.value) || 0)}
								size="small"
								sx={{ width: "100%" }}
							/>
						);
					}
					if (isCellEditing) {
						return (
							<TextField
								type="number"
								value={editValues[`${row.agencyName}-${col.backend}`] ?? editableWage}
								onChange={(e) => setEditValues((prev) => ({ ...prev, [`${row.agencyName}-${col.backend}`]: Number.parseFloat(e.target.value) || 0 }))}
								onBlur={() => handleCellBlur(row.agencyName, col.backend)}
								autoFocus
								size="small"
								sx={{ width: "100%" }}
							/>
						);
					}
					const displayValue = agencyCellText(col, roleWage.wage, { wage: String, hours: (hours) => t("agencyWages.hoursValue", { hours }) });
					return (
						<Box
							onDoubleClick={() => handleCellDoubleClick(row.agencyName, col.backend, roleWage.wage)}
							sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "text" }}
						>
							<Typography sx={{ color: INK.SECONDARY, fontSize: "14px" }}>{displayValue}</Typography>
						</Box>
					);
				},
			});
		}

		cols.push({
			field: "actions",
			headerName: t("agency.wages.actions"),
			flex: 8,
			minWidth: 100,
			sortable: false,
			renderCell: ({ row }) => {
				const isEditingRow = editingRowId === row.agencyName;
				if (isEditingRow) {
					return (
						<Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}>
							{/* Disabled while a save is in flight: re-clicking Save mid-request is what turned one
							    slow write into a duplicate row and a spurious error (DAT-577). */}
							<IconButton
								size="small"
								onClick={handleSaveRowEdit}
								color="success"
								disabled={isSaving}
								aria-label={t("agency.wages.save")}
								data-testid={`agency-wages-save-${row.agencyName}`}
							>
								{isSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
							</IconButton>
							<IconButton
								size="small"
								onClick={handleCancelRowEdit}
								disabled={isSaving}
								aria-label={t("agency.wages.cancel")}
								data-testid={`agency-wages-cancel-${row.agencyName}`}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>
					);
				}
				return (
					<Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}>
						<IconButton size="small" onClick={() => handleEditRow(row)} aria-label={t("agency.wages.edit")} data-testid={`agency-wages-edit-${row.agencyName}`}>
							<EditIcon fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							onClick={() => handleDeleteClick(row)}
							color="error"
							aria-label={t("delete")}
							data-testid={`agency-wages-delete-${row.agencyName}`}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				);
			},
		});

		return cols;
	}, [
		displayColumns,
		t,
		editingRowId,
		editedRow,
		editingCell,
		editValues,
		isSaving,
		handleCellDoubleClick,
		handleCellBlur,
		handleRowRoleWageChange,
		handleEditRow,
		handleSaveRowEdit,
		handleCancelRowEdit,
		handleDeleteClick,
	]);

	const emptyState = (
		<Box sx={{ p: 4, textAlign: "center" }}>
			<Typography variant="h6" sx={{ mb: 2 }}>
				{t("agency.wages.no.configured")}
			</Typography>
			<SystemButton buttonVariant="primary" onClick={handleOpenAddDialog}>
				{t("agency.wages.add.first")}
			</SystemButton>
		</Box>
	);

	return (
		<>
			<SystemDataGrid<AgencyRow>
				rows={agencyRows}
				columns={columns}
				getRowId={(r) => r.agencyName}
				sortingOptions={sortingOptions}
				defaultSortState={defaultSortState}
				localStorageKey="agencyWagesTableSorting"
				searchableColumns={["agencyName"]}
				searchPlaceholder={t("search.agency")}
				dataTestId="agency-wages-table"
				trailingToolbarNodes={
					<SystemButton buttonVariant="primary" onClick={handleOpenAddDialog}>
						{t("agency.wages.add")}
					</SystemButton>
				}
				dataGridProps={{
					// Larger row height so inline TextField inputs during row/cell edit don't clip.
					rowHeight: 56,
					slots: { noRowsOverlay: () => <>{emptyState}</> },
				}}
			/>

			<SystemDialog
				open={!!newAgency}
				onClose={handleCloseAddDialog}
				title={t("agency.wages.add")}
				confirm={t("agency.wages.save")}
				confirmButtonVariant="primary"
				// confirmLoading both spins and swallows clicks, so the dialog can't fire a second save.
				confirmLoading={isSaving}
				onConfirm={handleSaveNew}
				dataTestId="agency-wages-add-dialog"
				body={
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Autocomplete
							freeSolo
							options={availableAgencies}
							value={newAgency?.agencyName || ""}
							onInputChange={(_event, val) => handleNewAgencyInput(val)}
							renderInput={(params) => <TextField {...params} label={t("agency")} placeholder={t("agency.wages.new.agency")} size="small" />}
						/>
						{displayColumns.map((col) => (
							<TextField
								key={col.backend}
								type="number"
								label={col.display}
								value={newAgency?.roleWages?.[col.backend]?.wage ?? ""}
								onChange={(e) => handleNewRoleWageChange(col.backend, Number.parseFloat(e.target.value) || 0)}
								placeholder="0"
								size="small"
								fullWidth
							/>
						))}
					</Stack>
				}
			/>

			<SystemDialog
				open={deleteConfirmation.open}
				onClose={handleDeleteCancel}
				title={t("are.you.sure")}
				body={deleteConfirmation.row ? t("agency.wages.delete.confirm", { agencyName: deleteConfirmation.row.agencyName }) : ""}
				confirm={t("delete")}
				confirmButtonVariant="delete"
				onConfirm={handleDeleteConfirm}
				dataTestId="agency-wages-delete-dialog"
			/>
		</>
	);
};
